import type { RecipeExecutionResponse } from "@/services/api/recipes";

export function mapExecutionStateToHistoryStatus(
  state: string,
): RecipeExecutionResponse["status"] {
  switch (state) {
    case "completed":
      return "completed";
    case "failed":
      return "failed";
    case "cancelled":
      return "cancelled";
    case "running":
      return "running";
    case "waiting_for_form":
      return "waiting_for_form";
    case "pending":
    case "draft":
      return "pending";
    default:
      return "unknown";
  }
}

export type ExecutionBucket = "attention" | "active" | "waiting" | "queued" | "success" | "unknown";

export function getExecutionBucket(status: RecipeExecutionResponse["status"]): ExecutionBucket {
  switch (status) {
    case "failed":
    case "cancelled":
      return "attention";
    case "running":
      return "active";
    case "waiting_for_form":
      return "waiting";
    case "pending":
      return "queued";
    case "completed":
      return "success";
    default:
      return "unknown";
  }
}

export function isExecutionRerunnable(execution: Pick<RecipeExecutionResponse, "recipeId" | "status">) {
  return (
    Boolean(execution.recipeId) &&
    execution.status !== "running" &&
    execution.status !== "pending" &&
    execution.status !== "waiting_for_form"
  );
}

export function formatRuntimeDurationMs(value: number | undefined): string | null {
  if (typeof value !== "number" || value < 0) return null;
  if (value < 1000) return `${Math.round(value)}ms`;
  return `${(value / 1000).toFixed(1)}s`;
}

// Prefers the runtime-measured duration; only estimates from timestamps for
// a terminal execution when the backend did not report one. Returns null
// (never a ticking clock) for a still-running or waiting execution without a
// reported duration.
export function formatExecutionDuration(
  execution: Pick<RecipeExecutionResponse, "status" | "durationMs" | "startedAt" | "createdAt" | "completedAt">,
): string | null {
  const runtimeDuration = formatRuntimeDurationMs(execution.durationMs);
  if (runtimeDuration !== null) {
    return runtimeDuration;
  }

  if (execution.status === "running" || execution.status === "waiting_for_form") {
    return null;
  }

  if (!execution.completedAt) {
    return null;
  }

  const start = new Date(execution.startedAt || execution.createdAt).getTime();
  const end = new Date(execution.completedAt).getTime();
  const duration = end - start;

  if (duration < 1000) return `${Math.round(duration)}ms`;
  return `${(duration / 1000).toFixed(1)}s`;
}

// A missing measurement (undefined) is distinct from an actual zero — the
// caller renders the null case as "not available", never as 0.
export function formatUsageTokens(value: number | undefined, locale?: string): string | null {
  if (typeof value !== "number") return null;
  return new Intl.NumberFormat(locale).format(value);
}

export function formatUsageCost(value: number | undefined): string | null {
  if (typeof value !== "number") return null;
  return `$${value.toFixed(value < 0.01 ? 6 : 4)}`;
}
